import { useState } from "react";
import { Link } from "react-router-dom";
import { useSeo, SITE_ORIGIN } from "@/hooks/useSeo";
import Breadcrumbs from "@/components/feature/Breadcrumbs";
import { posts } from "@/mocks/posts";
import { zones } from "@/mocks/zones";

type TabKey = "hot" | "read" | "new";

const tabs: { key: TabKey; label: string; icon: string; note: string }[] = [
  { key: "hot", label: "综合热度", icon: "ri-fire-line", note: "阅读量与讨论集中度加权后的排序" },
  { key: "read", label: "阅读最多", icon: "ri-eye-line", note: "仅按累计阅读量从高到低排列" },
  { key: "new", label: "最新整理", icon: "ri-time-line", note: "按编辑最近一次整理的时间排列" },
];

function getZone(zoneId: string) {
  return zones.find((zone) => zone.id === zoneId);
}

function formatReads(count: number) {
  return count >= 10000 ? `${(count / 10000).toFixed(1)}万` : `${count}`;
}

export default function RankingPage() {
  const [active, setActive] = useState<TabKey>("hot");
  const activeTab = tabs.find((tab) => tab.key === active) ?? tabs[0];

  const ranked = [...posts].sort((a, b) => {
    if (active === "new") return a.date < b.date ? 1 : -1;
    if (active === "read") return b.readCount - a.readCount;
    return b.readCount + (b.hot ? 20000 : 0) - (a.readCount + (a.hot ? 20000 : 0));
  });

  const interpretation = posts.find((post) => post.zoneId === "z6") ?? posts[5];

  useSeo({
    title: "内容热榜 - 爆料91专区多维度排行与解读",
    description:
      "爆料91内容热榜按综合热度、阅读量、整理时间三个维度排名，同一批专区内容换个维度顺序就不同，并附榜单变化解读，帮你看清哪些合集在集中被讨论。",
    keywords: "爆料91热榜,爆料91榜单,爆料91专区排行,内容热度,榜单解读",
    path: "/ranking",
    type: "website",
    jsonLd: [
      {
        "@context": "https://schema.org",
        "@type": "CollectionPage",
        name: "爆料91内容热榜",
        url: `${SITE_ORIGIN}/ranking`,
        inLanguage: "zh-CN",
        description: "按多个维度排列的爆料91专区内容榜单。",
      },
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "首页", item: `${SITE_ORIGIN}/` },
          { "@type": "ListItem", position: 2, name: "内容热榜", item: `${SITE_ORIGIN}/ranking` },
        ],
      },
      {
        "@context": "https://schema.org",
        "@type": "ItemList",
        name: "爆料91内容热榜",
        itemListElement: ranked.slice(0, 6).map((post, index) => ({
          "@type": "ListItem",
          position: index + 1,
          name: post.title,
          url: `${SITE_ORIGIN}/post/${post.id}`,
        })),
      },
    ],
  });

  return (
    <>
      <section className="w-full bg-background-100 border-b border-background-200">
        <div className="w-full px-4 md:px-8 pt-6 pb-10 md:pb-14">
          <Breadcrumbs items={[{ label: "首页", to: "/" }, { label: "内容热榜" }]} />
          <div className="mt-6 max-w-3xl">
            <span className="inline-flex items-center gap-2 text-xs font-label tracking-[0.2em] uppercase text-primary-700">
              <span className="w-5 h-px bg-primary-500"></span>
              Ranking
            </span>
            <h1 className="mt-3 font-heading font-black text-3xl md:text-4xl text-foreground-950">
              爆料91内容热榜
            </h1>
            <p className="mt-4 text-sm md:text-base leading-relaxed text-foreground-600">
              同一批内容，换个维度排列，顺序往往完全不同。榜单只回答「多少人在看」，
              回答不了「看完留下什么」，所以我们把排序依据一并写出来，方便你自己判断。
            </p>
          </div>
        </div>
      </section>

      <section className="w-full bg-background-50">
        <div className="w-full px-4 md:px-8 py-10 md:py-14">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-10">
            <div className="lg:col-span-8">
              <div className="flex flex-wrap items-center gap-1 p-1 rounded-full border border-background-300 bg-background-100 w-fit">
                {tabs.map((tab) => (
                  <button
                    key={tab.key}
                    type="button"
                    onClick={() => setActive(tab.key)}
                    className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm whitespace-nowrap cursor-pointer transition-colors ${
                      active === tab.key
                        ? "bg-primary-600 text-background-50"
                        : "text-foreground-700 hover:text-foreground-950"
                    }`}
                  >
                    <i className={tab.icon}></i>
                    {tab.label}
                  </button>
                ))}
              </div>

              <p className="mt-4 text-sm text-foreground-500">排序依据：{activeTab.note}</p>

              <ol className="mt-6 flex flex-col divide-y divide-background-200 rounded-lg border border-background-200 bg-background-50 overflow-hidden">
                {ranked.map((post, index) => {
                  const zone = getZone(post.zoneId);
                  return (
                    <li key={post.id}>
                      <Link
                        to={`/post/${post.id}`}
                        className="group flex items-center gap-4 md:gap-6 px-5 md:px-6 py-4 hover:bg-background-100 transition-colors cursor-pointer"
                      >
                        <span
                          className={`font-label text-2xl md:text-3xl w-10 md:w-12 shrink-0 ${
                            index === 0
                              ? "text-primary-600"
                              : index < 3
                                ? "text-accent-700"
                                : "text-foreground-300"
                          }`}
                        >
                          {String(index + 1).padStart(2, "0")}
                        </span>
                        <span className="flex-1 min-w-0">
                          <span className="block font-heading font-semibold text-base md:text-lg text-foreground-950 line-clamp-1 group-hover:text-primary-700 transition-colors">
                            {post.title}
                          </span>
                          <span className="mt-1 block text-xs text-foreground-500">
                            {zone ? zone.name : "专区"} · {post.date} · {post.author}
                            {post.hot && <span className="ml-2 text-primary-600">· 热门</span>}
                          </span>
                        </span>
                        <span className="hidden sm:flex items-center gap-1 text-sm text-foreground-600 shrink-0">
                          <i className="ri-eye-line"></i>
                          {formatReads(post.readCount)}
                        </span>
                        <i className="ri-arrow-right-line text-foreground-400 group-hover:text-primary-600 group-hover:translate-x-1 transition-all"></i>
                      </Link>
                    </li>
                  );
                })}
              </ol>
            </div>

            <aside className="lg:col-span-4 flex flex-col gap-6">
              <div className="rounded-lg border border-background-200 bg-background-100 p-5">
                <h2 className="font-heading font-bold text-lg text-foreground-950">榜单怎么看</h2>
                <ul className="mt-4 flex flex-col gap-4 text-sm text-foreground-600">
                  <li className="flex gap-3">
                    <i className="ri-checkbox-circle-line text-accent-700 text-lg shrink-0"></i>
                    热度榜适合快速了解当下话题集中在哪里，但不代表内容更值得读。
                  </li>
                  <li className="flex gap-3">
                    <i className="ri-checkbox-circle-line text-accent-700 text-lg shrink-0"></i>
                    阅读榜更稳定，适合判断一条内容是不是持久被需要。
                  </li>
                  <li className="flex gap-3">
                    <i className="ri-checkbox-circle-line text-accent-700 text-lg shrink-0"></i>
                    最新榜看的是整理效率，热门方向基本每天都会有新条目。
                  </li>
                </ul>
              </div>

              <div className="rounded-lg border border-background-200 bg-background-100 overflow-hidden">
                <div className="w-full aspect-[16/9] bg-background-200">
                  <img
                    src={interpretation.cover}
                    alt={`${interpretation.title} 配图`}
                    title={`${interpretation.title} - 爆料91专区合集`}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
                <div className="p-5">
                  <span className="text-xs text-primary-700 font-medium">榜单解读</span>
                  <h2 className="mt-2 font-heading font-bold text-lg text-foreground-950 leading-snug">
                    {interpretation.title}
                  </h2>
                  <p className="mt-2 text-sm leading-relaxed text-foreground-600 line-clamp-3">
                    {interpretation.excerpt}
                  </p>
                  <Link
                    to={`/post/${interpretation.id}`}
                    className="mt-4 inline-flex items-center gap-1 text-sm text-primary-700 font-medium cursor-pointer hover:gap-2 transition-all"
                  >
                    阅读这篇解读
                    <i className="ri-arrow-right-line"></i>
                  </Link>
                </div>
              </div>

              <div className="rounded-lg border border-accent-200 bg-accent-50 p-5">
                <h2 className="font-heading font-bold text-lg text-foreground-950">想按专区看？</h2>
                <p className="mt-2 text-sm leading-relaxed text-foreground-700">
                  榜单只挑出了头部内容。如果你想完整浏览某个方向，从专区入口进去更合适。
                </p>
                <Link
                  to="/zones"
                  className="mt-4 inline-flex items-center justify-center gap-2 h-10 px-4 w-full rounded-md bg-primary-600 text-background-50 text-sm whitespace-nowrap cursor-pointer hover:bg-primary-700 transition-colors"
                >
                  <i className="ri-layout-grid-fill"></i>
                  前往专区总览
                </Link>
              </div>
            </aside>
          </div>
        </div>
      </section>
    </>
  );
}